import { ComponentProps, FC } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { getLogger } from '@/shared/lib/logger.client';
import { Optional } from '@/shared/model/Optional';
import { DEFAULT_CLOSE_ANIMATION_DURATION } from './constants';

const logger = getLogger('modal/store');

export type ModalOptions = {
    /**
     * Position to where should modal be mounted.
     *
     * Positions can be:
     * - `above` - Show modal above all currently open modals (default)
     * - `replace` - Hide all currently open modals and show only this one
     */
    position?: 'above' | 'replace';

    /** Time it takes to play exiting animation in ms */
    closeAnimationDuration?: number;
};

type ModalData = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component: FC<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    componentProps: ComponentProps<any>;
    closeAnimationDuration: number;
    isClosing: boolean;
};

export type Store = {
    /** Object holding all visible modals with `[id]: ModalData` binding */
    data: Record<string, ModalData>;

    /** Last used id (incremented with every show) */
    lastId: number;

    /**
     *
     * @param id  ID of modal instance
     * @param closeGuard Function that is called before modal is closed.
     * If it returns `false` or `Promise` resolving to `false`, modal will not be closed.
     * This is useful for preventing modal closing when some action is not completed yet
     * (e.g. form validation, unsaved changes, etc.).
     * @returns - boolean
     */
    setCloseGuard: (id: number, closeGuard: () => boolean | Promise<boolean>) => void;

    /**
     * Shows provided modal component
     * @param ModalComponent React component
     * @param props Props to be passed to modal component
     * @param options Options specifying modal behavior
     * @returns `id` of the created modal instance
     */
    show<M extends FC<ComponentProps<M>>>(
        ModalComponent: M,
        // @ts-expect-error string satisfies the constraint
        props: Optional<ComponentProps<M>, 'onClose' | 'isClosing' | 'modalId'>,
        options?: ModalOptions,
    ): Promise<number>;

    /**
     * Hides modal
     * @param id ID of modal instance
     * @param skipGuardCheck If `true`, modal will be closed without checking closeGuard function,
     */
    hide(id: number, skipGuardCheck?: boolean): Promise<boolean>;

    /**
     * Hides all modals
     */
    hideAll(): Promise<boolean>;

    /** Functions that are called before modal is closed.
     * If it returns `false` or `Promise` resolving to `false`, modal will not be closed.
     */
    closeGuards: Record<number, () => boolean | Promise<boolean>>;
};

export const useModalsStore = create<Store>()(
    immer((set, get) => ({
        data: {},
        lastId: 0,
        closeGuards: {},
        setCloseGuard: (id, closeGuard) => {
            set((state) => {
                state.closeGuards[id] = closeGuard;
            });
        },
        show: async (ModalComponent, props, options) => {
            if (options?.position === 'replace') {
                if (!(await get().hideAll())) {
                    return get().lastId;
                }
            }
            set((state) => {
                const newId = state.lastId + 1;
                state.data[newId] = {
                    closeAnimationDuration:
                        options?.closeAnimationDuration ?? DEFAULT_CLOSE_ANIMATION_DURATION,
                    Component: ModalComponent,
                    componentProps: props,
                    isClosing: false,
                };
                state.lastId = newId;
            });
            return get().lastId;
        },
        hide: async (id, skipGuardCheck) => {
            const data = get().data[id];
            if (!data) {
                logger.error(`Data for modal ID ${id} not found.`);
                return false;
            }
            const closeGuard = get().closeGuards[id];
            if (closeGuard && !skipGuardCheck) {
                if (!(await closeGuard())) {
                    return false;
                }
            }
            set((state) => {
                state.data[id].isClosing = true;
            });
            return new Promise<boolean>((resolve) => {
                setTimeout(() => {
                    set((state) => {
                        delete state.data[id];
                        delete state.closeGuards[id];
                    });
                    resolve(true);
                }, data.closeAnimationDuration);
            });
        },
        hideAll: async () => {
            const { data, hide } = get();
            return (await Promise.all(Object.keys(data).map((id) => hide(parseInt(id))))).every(
                (result) => result === true,
            );
        },
    })),
);
